import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useGetCountries } from "@/api/country/useCountryAPI";
import {
  useGetDataProviders,
  useGetDatasetDataMutation,
  useGetDatasetListQuery,
  useGetDatasetMetadataQuery
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon, LoaderIcon, SearchIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import Modal from "@/components/ui/modal";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { COUNTRY_DATA } from "@/constants/dataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDebounce } from "@/hooks/useDebounce";
import { usePagination } from "@/hooks/usePagination";
import { RootState } from "@/store";
import { setSelectedDatasetExplorer } from "@/store/slice/datasetSlice";
import { CountryCode, Source, SpatialAggregationType } from "@/types/common";
import { DatasetExploreItem, License } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import DatasetRightAttrFilter from "../datasetAttributeFilter/DatasetRightAttrFilter";
import DatasetCard from "../datasetCard";
import DatasetFilterSection from "./DataSourceFilter";
import { Preview } from "./Preview";
import useSessionStorage from "@/hooks/store/useSessionStorage";

const TEST_ID_PREFIX = "dataset-explorer";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export interface DatasetExplorerSessionStorageData {
  searchQuery: string;
  searchDataSource: string;
  country: CountryCode;
  dataSource: Source;
  spatialAggregationType: SpatialAggregationType | "all";
  published: boolean;
  currentPage: number;
  pageSize: number;
  license: License | "all";
  providerId: string;
}

export const DEFAULT_SESSION_DATA: DatasetExplorerSessionStorageData = {
  searchQuery: "",
  searchDataSource: "",
  country: "",
  dataSource: "bvt",
  spatialAggregationType: "all",
  published: true,
  currentPage: 1,
  pageSize: 10,
  license: "all",
  providerId: ""
};

const SESSION_STORAGE_KEY = "dataset-explorer-modal";

const DatasetExplorerPopup = ({ isOpen, onClose }: Props) => {
  const {
    sessionValue: sessionStorageData,
    setSessionItem,
    removeSessionItem
  } = useSessionStorage<DatasetExplorerSessionStorageData>(
    SESSION_STORAGE_KEY,
    DEFAULT_SESSION_DATA
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const prevData = location?.state;
  const { isUseNewDatasetExplorerModal, isMultipleSearchTableDataset } =
    useAppFeatures();
  const { showLoadingOverlay } = useSelector((state: RootState) => state.ui);
  const {
    pageSize,
    setPageSize: originalSetPageSize,
    currentPage,
    setCurrentPage: originalSetCurrentPage
  } = usePagination({
    initialPageSize:
      sessionStorageData?.pageSize ?? DEFAULT_SESSION_DATA.pageSize,
    initialPage:
      sessionStorageData?.currentPage ?? DEFAULT_SESSION_DATA.currentPage
  });

  const { showError } = useLoadingAndToast();

  const { data: dataProviders } = useGetDataProviders({
    enabled: isUseNewDatasetExplorerModal
  });

  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);

  const [dataSource, setDataSource] = useState<Source>(
    showCatchmentPopup
      ? "user"
      : (sessionStorageData?.dataSource ?? DEFAULT_SESSION_DATA.dataSource)
  );
  const [spatialAgg, setSpatialAgg] = useState<SpatialAggregationType | "all">(
    showCatchmentPopup
      ? "poi"
      : (sessionStorageData?.spatialAggregationType ??
          DEFAULT_SESSION_DATA.spatialAggregationType)
  );
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(
    sessionStorageData?.providerId
      ? sessionStorageData.providerId.split(",")
      : []
  );
  const [license, setLicense] = useState<License | "all">(
    sessionStorageData?.license ?? DEFAULT_SESSION_DATA.license
  );
  const [isButtonPreviewEnabled, setIsButtonPreviewEnabled] =
    useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);

  const [selectedCountry, setSelectedCountry] = useState<{
    label: string;
    value: CountryCode;
  }>({
    label: sessionStorageData?.country
      ? COUNTRY_DATA.find(
          (country) => country.value === sessionStorageData.country
        )?.label || "All"
      : "All",
    value: sessionStorageData?.country ?? DEFAULT_SESSION_DATA.country
  });
  const [selectedDataset, setSelectedDataset] =
    useState<DatasetExploreItem | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>(
    sessionStorageData?.searchQuery ?? DEFAULT_SESSION_DATA.searchQuery
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const { data: selectedDatasetMetadata } = useGetDatasetMetadataQuery(
    selectedDataset?.id ?? ""
  );
  const { data: userProfileData } = useGetUserProfileAPI();

  const {
    mutateAsync: getDatasetDataPost,
    data: datasetData,
    isPending: isLoadingDatasetData
  } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone,
    options: {
      onError: () => {
        showError(
          <NotificationContent
            title="Something Went Wrong"
            description="We’re sorry, we cannot proceed with your request. Please try again."
          />
        );
      }
    }
  });

  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries();

  const countryOptions =
    countryData?.map((country) => ({
      label: country.name.toLowerCase() === "global" ? "All" : country.name,
      value: country.id === "global" ? "" : country.id // "global" is represented as empty string in API
    })) || [];

  const { data: datasetList, isLoading: isLoadingDatasetList } =
    useGetDatasetListQuery(
      {
        q: debouncedSearchQuery?.length >= 2 ? debouncedSearchQuery : "", // Allow search when keyword size >=2
        country: selectedCountry.value,
        source: dataSource || undefined,
        spatial_aggregation_type: spatialAgg || "all",
        published: true,
        page: currentPage,
        per_page: pageSize,
        ...(isUseNewDatasetExplorerModal && dataSource === "bvt"
          ? {
              license: license === "all" ? "regular,premium" : license,
              provider_id:
                selectedDataSources.length > 0
                  ? selectedDataSources.join(",")
                  : undefined
            }
          : {})
      },
      ...(isUseNewDatasetExplorerModal
        ? [
            {
              gcTime: 0,
              staleTime: 0,
              refetchOnMount: true
            }
          ]
        : [])
    );

  const handleSelectData = async (data: DatasetExploreItem) => {
    if (data.id === selectedDataset?.id) return;
    setSelectedDataset(data);
    setIsButtonPreviewEnabled(true);

    if (showCatchmentPopup) {
      await getDatasetDataPost({
        id: data.id
      });
    }
  };

  useEffect(() => {
    if (prevData) {
      setSelectedDataset(prevData);
      setIsPreview(true);
    }
  }, [prevData]);

  const dataSourceProviders = (dataProviders ?? [])?.map((ctx) => ({
    id: ctx.id,
    label: ctx.name,
    icon: <img src={ctx.logo_asset_url} alt={ctx.name} className="w-6 h-6" />
  }));

  const handleChange =
    <T,>(setter: (value: T) => void) =>
    (e: T) => {
      setter(e);
      setSelectedDataset(null);
      setIsButtonPreviewEnabled(false);
    };

  const setPageSize = useCallback(
    (value: number) => {
      originalSetPageSize(value);
      setSessionItem({
        ...sessionStorageData,
        pageSize: value
      });
    },
    [originalSetPageSize, setSessionItem, sessionStorageData]
  );

  const setCurrentPage = useCallback(
    (value: number) => {
      originalSetCurrentPage(value);
      setSessionItem({
        ...sessionStorageData,
        currentPage: value
      });
    },
    [sessionStorageData, originalSetCurrentPage, setSessionItem]
  );

  const setCountry = useCallback(
    ({ label, value }: { label: string; value: CountryCode }) => {
      setSelectedCountry({
        label: label,
        value: value
      });
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        country: value,
        currentPage: 1
      });
    },
    [
      originalSetCurrentPage,
      setSelectedCountry,
      sessionStorageData,
      setSessionItem
    ]
  );

  const setSearchDataset = useCallback(
    (value: string) => {
      setSearchQuery(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        searchQuery: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, setSearchQuery, sessionStorageData, setSessionItem]
  );

  const setSelectLicense = useCallback(
    (value: License | "all") => {
      setLicense(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        license: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, setLicense, sessionStorageData, setSessionItem]
  );

  const setSelectDataSource = useCallback(
    (value: Source) => {
      setDataSource(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        dataSource: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, setDataSource, sessionStorageData, setSessionItem]
  );

  const setSpatialAggSession = useCallback(
    (value: SpatialAggregationType | "all") => {
      setSpatialAgg(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        spatialAggregationType: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, setSpatialAgg, sessionStorageData, setSessionItem]
  );

  const handleClose = () => {
    if (showLoadingOverlay) return;
    onClose();
    setIsPreview(false);
    location.state = null;
    dispatch(setSelectedDatasetExplorer(null));
    removeSessionItem();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      containerClassname="w-[90vw] h-[90vh] flex flex-col"
    >
      {!isPreview ? (
        <>
          {/* Header */}
          <div className="flex flex-row justify-between p-6 pb-4">
            <h2 className="text-txtHeading font-bold text-[20px]">
              Dataset Explorer
            </h2>
            <button
              className="bg-transparent border-0"
              onClick={handleClose}
              aria-label="Close Dataset Explorer Modal"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
          {/* Content */}
          <div className="flex flex-1 gap-5 p-6 pt-0 overflow-hidden">
            {/* Left content */}
            <div className="flex flex-col w-[80%] overflow-hidden">
              <div className="flex items-center gap-1 border-[1px] border-gainsboro rounded-[12px] px-3 h-[40px] shrink-0">
                <SearchIcon className="fill-light-gray w-[16px]" />
                <input
                  name={`${TEST_ID_PREFIX}-search-dataset`}
                  className="w-full focus:outline-none"
                  placeholder="Search datasets"
                  value={searchQuery}
                  onChange={(e) => setSearchDataset(e.target.value)}
                  data-testid={`${TEST_ID_PREFIX}-search-dataset-input`}
                />
              </div>
              <div className="flex flex-1 gap-4 mt-3 overflow-hidden">
                {/* Filter Section */}
                <DatasetFilterSection
                  countryOptions={countryOptions}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={handleChange(setCountry)}
                  dataSource={dataSource}
                  spatialAgg={spatialAgg}
                  license={license}
                  selectedDataSources={selectedDataSources}
                  setDataSource={handleChange(setSelectDataSource)}
                  setSpatialAgg={handleChange(setSpatialAggSession)}
                  setCurrentPage={handleChange(setCurrentPage)}
                  setLicense={handleChange(setSelectLicense)}
                  setSelectedDataSources={handleChange(setSelectedDataSources)}
                  isUseNewDatasetExplorerModal={isUseNewDatasetExplorerModal}
                  dataSourceProviders={dataSourceProviders}
                  showCatchmentPopup={showCatchmentPopup}
                  sessionStorageData={sessionStorageData}
                  setSessionItem={setSessionItem}
                />
                {/* Data list */}
                <div className="flex flex-col w-[70%] overflow-hidden">
                  <span className="text-sm shrink-0">
                    {isLoadingDatasetList || isLoadingCountryData
                      ? "Loading..."
                      : `All Datasets (${datasetList?.data.length ?? 0} of ${
                          datasetList?.meta.pagination.total ?? 1
                        })`}
                  </span>
                  <div className="h-full overflow-y-auto custom-scrollbar">
                    <div className="flex flex-col items-center h-full">
                      <div className="grid w-full grid-cols-2 gap-3 py-2">
                        {datasetList?.data?.map((item) => (
                          <DatasetCard
                            key={item.id}
                            data={item}
                            isSelected={selectedDataset?.id === item.id}
                            handleSelectData={handleSelectData}
                            isUseNewDatasetExplorerModal={
                              isUseNewDatasetExplorerModal
                            }
                          />
                        ))}
                      </div>
                      {(isLoadingDatasetList || isLoadingCountryData) && (
                        <div className="flex flex-col items-center gap-2 my-auto text-sm text-center text-dim-gray">
                          <LoaderIcon className="animate-spin" />
                          <p className="text-xs text-txtPlaceholder">
                            Loading...
                          </p>
                        </div>
                      )}
                      {datasetList?.data.length === 0 && (
                        <div className="flex flex-col items-center gap-1 my-auto text-sm text-center text-dim-gray">
                          <SearchIcon className="w-8 fill-dim-gray" />
                          <p className="text-sm font-semibold text-txtPlaceholder">
                            Data Not Found
                          </p>
                          <p className="text-txtBody text-x">
                            We couldn't find any data matching "
                            {debouncedSearchQuery}" Try using different <br />
                            keywords to refine your search.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Pagination */}
                  <div className="flex-1 mt-4">
                    <div
                      className={twMerge(
                        "flex items-center",
                        isUseNewDatasetExplorerModal
                          ? "justify-between"
                          : "justify-center"
                      )}
                    >
                      {isUseNewDatasetExplorerModal && (
                        <div className="flex items-center mr-auto text-sm text-txtBody">
                          <Select
                            value={String(pageSize)}
                            onValueChange={(value) => {
                              const newPageSize = Number(value);
                              setCurrentPage(1);
                              setPageSize(newPageSize);
                              setSelectedDataset(null);
                              setIsButtonPreviewEnabled(false);
                            }}
                          >
                            <SelectTrigger className="h-8 w-fit">
                              <SelectValue placeholder="10" />
                            </SelectTrigger>
                            <SelectContent className="z-[9999]" side="top">
                              {[10, 25, 50, 100].map((pageSize) => (
                                <SelectItem
                                  key={pageSize}
                                  value={String(pageSize)}
                                >
                                  {pageSize}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="ml-2">
                            of {datasetList?.meta.pagination.total || 1}{" "}
                            Datasets
                          </span>
                        </div>
                      )}
                      <Pagination
                        currentPage={currentPage}
                        totalPages={
                          datasetList?.meta.pagination.total_pages || 1
                        }
                        onPageChange={handleChange(setCurrentPage)}
                      />
                    </div>
                  </div>
                  {isUseNewDatasetExplorerModal && (
                    <div className="flex justify-end mt-4">
                      <Button
                        data-testid="preview-button"
                        variant="outline"
                        className="py-2.5 h-auto w-[200px] rounded-xl font-semibold text-sm"
                        onClick={() => {
                          if (isButtonPreviewEnabled) {
                            setIsPreview(true);
                          }
                        }}
                        disabled={!isButtonPreviewEnabled}
                      >
                        Preview Dataset
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right content */}
            <DatasetRightAttrFilter
              key={selectedDatasetMetadata?.id}
              disabled={!selectedDataset || isLoadingDatasetData}
              source={selectedDataset?.source}
              selectedDataset={selectedDatasetMetadata ?? null}
              onClose={onClose}
              isSubscribed={selectedDataset?.is_subscribed}
              isLoading={isLoadingDatasetData}
              totalRows={datasetData?.rows?.length}
              datasetData={datasetData}
            />
          </div>
        </>
      ) : (
        <Preview
          onBack={() => {
            setSelectedDataset(null);
            setIsPreview(false);
            setIsButtonPreviewEnabled(false);
          }}
          isMultipleSearchTableDataset={isMultipleSearchTableDataset}
          selectedDataset={selectedDataset ?? null}
          dataSource={dataSource}
        />
      )}
    </Modal>
  );
};

export default DatasetExplorerPopup;
